/* eslint-disable max-classes-per-file */
import { dropboxAppKey } from 'config/constants';
import { isImportableMime, mimeFromFileName } from 'helpers/import-mime';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DropboxFile = {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  modifiedTime?: string;
  createdTime?: string;
  // Dropbox addresses entries by path; keep both forms (lowercased for API
  // calls, display-cased for breadcrumbs).
  pathLower: string;
  pathDisplay: string;
  isFolder: boolean;
  isImportable: boolean;
};

export type DropboxListResponse = {
  files: DropboxFile[];
  cursor?: string;
};

export type DownloadedDropboxFile = {
  blob: Blob;
  name: string;
  mimeType: string;
  size: number;
  modifiedTime?: string;
};

// The Dropbox root folder (path '' in the API).
export const DROPBOX_ROOT: DropboxFile = {
  id: '',
  name: 'Dropbox',
  mimeType: 'inode/directory',
  pathLower: '',
  pathDisplay: '',
  isFolder: true,
  isImportable: false,
};

const normalizeEntry = (raw: any): DropboxFile => {
  const isFolder = raw['.tag'] === 'folder';
  const mimeType = isFolder ? 'inode/directory' : mimeFromFileName(raw.name);
  return {
    id: raw.id,
    name: raw.name,
    mimeType,
    size: raw.size != null ? Number(raw.size) : undefined,
    modifiedTime: raw.client_modified || raw.server_modified,
    pathLower: raw.path_lower ?? '',
    pathDisplay: raw.path_display ?? raw.path_lower ?? '',
    isFolder,
    isImportable: !isFolder && isImportableMime(mimeType),
  };
};

// ---------------------------------------------------------------------------
// OAuth 2 with PKCE (popup + /dropbox-callback + postMessage)
// ---------------------------------------------------------------------------

export class DropboxAuthCancelled extends Error {
  constructor() {
    super('Dropbox sign-in cancelled');
    this.name = 'DropboxAuthCancelled';
  }
}

export const DROPBOX_SCOPES =
  'account_info.read files.metadata.read files.content.read';

const AUTHORIZE_URL = 'https://www.dropbox.com/oauth2/authorize';
const TOKEN_URL = 'https://api.dropboxapi.com/oauth2/token';
const API_BASE = 'https://api.dropboxapi.com/2';
const CONTENT_BASE = 'https://content.dropboxapi.com/2';

const redirectUri = () => `${window.location.origin}/dropbox-callback`;

// Module-level cache (like helpers/google-drive.ts): tokens live for the tab's
// lifetime only; nothing is persisted.
let cachedAccessToken: string | null = null;
let cachedTokenExpiresAt = 0;
let cachedRefreshToken: string | null = null;

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

const randomString = (length: number): string => {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (v) => alphabet[v % alphabet.length]).join('');
};

const sha256Challenge = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
};

const requireAppKey = (): string => {
  if (!dropboxAppKey) {
    throw new Error('VITE_APP_DROPBOX_APP_KEY is not configured');
  }
  return dropboxAppKey;
};

const storeTokenResponse = (data: any) => {
  cachedAccessToken = data.access_token;
  const expiresIn = Number(data.expires_in) || 14400;
  // Refresh a bit early to avoid edge-of-expiry races.
  cachedTokenExpiresAt = Date.now() + (expiresIn - 60) * 1000;
  if (data.refresh_token) cachedRefreshToken = data.refresh_token;
};

const exchangeToken = async (params: Record<string, string>): Promise<void> => {
  const body = new URLSearchParams({ client_id: requireAppKey(), ...params });
  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    let snippet = '';
    try {
      snippet = (await response.text()).slice(0, 240);
    } catch {
      // ignore
    }
    throw new Error(
      `Dropbox token exchange failed (${response.status}): ${snippet}`,
    );
  }
  storeTokenResponse(await response.json());
};

const refreshAccessToken = async (): Promise<boolean> => {
  if (!cachedRefreshToken) return false;
  try {
    await exchangeToken({
      grant_type: 'refresh_token',
      refresh_token: cachedRefreshToken,
    });
    return true;
  } catch {
    cachedRefreshToken = null;
    return false;
  }
};

type CallbackMessage = {
  type?: string;
  code?: string;
  state?: string;
  error?: string;
};

const interactiveSignIn = async (forceReapprove: boolean): Promise<void> => {
  const appKey = requireAppKey();
  const verifier = randomString(64);
  const challenge = await sha256Challenge(verifier);
  const state = randomString(24);

  const url = new URL(AUTHORIZE_URL);
  url.searchParams.set('client_id', appKey);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('token_access_type', 'offline');
  url.searchParams.set('scope', DROPBOX_SCOPES);
  url.searchParams.set('state', state);
  if (forceReapprove) url.searchParams.set('force_reapprove', 'true');

  const popup = window.open(
    url.toString(),
    'dropbox-oauth',
    'width=680,height=760',
  );
  if (!popup) throw new DropboxAuthCancelled();

  const code = await new Promise<string>((resolve, reject) => {
    let settled = false;
    let closedPoll: ReturnType<typeof setInterval> | undefined;
    let onMessage: (event: MessageEvent<CallbackMessage>) => void = () => {};

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      if (closedPoll) clearInterval(closedPoll);
      fn();
    };

    onMessage = (event: MessageEvent<CallbackMessage>) => {
      if (event.origin !== window.location.origin) return;
      const { data } = event;
      if (data?.type !== 'dropbox-oauth') return;
      if (data.state !== state) return;
      if (data.error || !data.code) {
        finish(() =>
          reject(
            data.error === 'access_denied'
              ? new DropboxAuthCancelled()
              : new Error(data.error || 'Dropbox sign-in failed'),
          ),
        );
        return;
      }
      finish(() => resolve(data.code!));
    };

    // The popup navigates cross-origin, so the only "cancelled" signal we
    // get is the window closing without ever posting back.
    closedPoll = setInterval(() => {
      if (popup.closed) {
        // Give a just-sent postMessage a beat to arrive before rejecting.
        setTimeout(() => finish(() => reject(new DropboxAuthCancelled())), 300);
      }
    }, 400);

    window.addEventListener('message', onMessage);
  });

  await exchangeToken({
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    redirect_uri: redirectUri(),
  });
};

/**
 * Cached token → refresh-token grant → interactive popup, mirroring
 * signInToDrive. User-cancel surfaces as DropboxAuthCancelled.
 */
export const signInToDropbox = async (options?: {
  forceReapprove?: boolean;
}): Promise<string> => {
  if (
    !options?.forceReapprove &&
    cachedAccessToken &&
    Date.now() < cachedTokenExpiresAt
  ) {
    return cachedAccessToken;
  }
  if (!options?.forceReapprove && (await refreshAccessToken())) {
    return cachedAccessToken!;
  }
  await interactiveSignIn(options?.forceReapprove ?? false);
  return cachedAccessToken!;
};

export const signOutFromDropbox = async (): Promise<void> => {
  const token = cachedAccessToken;
  cachedAccessToken = null;
  cachedTokenExpiresAt = 0;
  cachedRefreshToken = null;
  if (!token) return;
  try {
    await fetch(`${API_BASE}/auth/token/revoke`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Best-effort; local cache is already cleared.
  }
};

export const getCachedDropboxToken = (): string | null => cachedAccessToken;

// ---------------------------------------------------------------------------
// REST calls
// ---------------------------------------------------------------------------

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Non-ASCII characters aren't valid in HTTP headers; Dropbox requires the
// Dropbox-API-Arg JSON to be escaped to plain ASCII.
const asciiEscapeJson = (value: unknown): string =>
  JSON.stringify(value).replace(
    /[\u0080-\uffff]/g,
    (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`,
  );

const dropboxFetch = async (
  url: string,
  init: RequestInit,
  attempt = 0,
): Promise<Response> => {
  const token = await signInToDropbox();
  const response = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401 && attempt === 0) {
    // Short-lived token expired mid-run: refresh once and retry.
    cachedAccessToken = null;
    cachedTokenExpiresAt = 0;
    return dropboxFetch(url, init, attempt + 1);
  }

  if (response.status === 429 && attempt < 3) {
    const retryAfter = Number(response.headers.get('Retry-After')) || 1;
    await sleep(retryAfter * 1000);
    return dropboxFetch(url, init, attempt + 1);
  }

  if (!response.ok) {
    let snippet = '';
    try {
      snippet = (await response.text()).slice(0, 240);
    } catch {
      // ignore
    }
    throw new Error(
      `Dropbox API ${response.status} ${response.statusText}: ${snippet}`,
    );
  }
  return response;
};

const rpc = async (path: string, body: unknown): Promise<any> => {
  const response = await dropboxFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
};

export const listFolder = async (
  path: string,
  cursor?: string,
): Promise<DropboxListResponse> => {
  const data = cursor
    ? await rpc('/files/list_folder/continue', { cursor })
    : await rpc('/files/list_folder', {
        path,
        recursive: false,
        limit: 200,
        include_non_downloadable_files: false,
      });
  return {
    files: (data.entries || []).map(normalizeEntry),
    cursor: data.has_more ? data.cursor : undefined,
  };
};

/**
 * Every importable file inside a folder and its subfolders, using Dropbox's
 * native recursive listing.
 */
export const listFolderRecursive = async (
  path: string,
): Promise<DropboxFile[]> => {
  const importable: DropboxFile[] = [];
  let data = await rpc('/files/list_folder', {
    path,
    recursive: true,
    limit: 500,
    include_non_downloadable_files: false,
  });
  for (;;) {
    (data.entries || []).forEach((raw: any) => {
      const entry = normalizeEntry(raw);
      if (!entry.isFolder && entry.isImportable) importable.push(entry);
    });
    if (!data.has_more) break;
    // eslint-disable-next-line no-await-in-loop
    data = await rpc('/files/list_folder/continue', { cursor: data.cursor });
  }
  return importable;
};

export const searchDropbox = async (
  query: string,
  cursor?: string,
  path?: string,
): Promise<DropboxListResponse> => {
  const data = cursor
    ? await rpc('/files/search/continue_v2', { cursor })
    : await rpc('/files/search_v2', {
        query,
        options: {
          path: path || '',
          max_results: 100,
          filename_only: true,
          file_status: 'active',
        },
      });
  const files = (data.matches || [])
    .map((match: any) => match?.metadata?.metadata)
    .filter(Boolean)
    .map(normalizeEntry);
  return { files, cursor: data.has_more ? data.cursor : undefined };
};

export const downloadDropboxFile = async (
  file: DropboxFile,
): Promise<DownloadedDropboxFile> => {
  // Address by id (stable, header-safe) rather than path.
  const response = await dropboxFetch(`${CONTENT_BASE}/files/download`, {
    method: 'POST',
    headers: { 'Dropbox-API-Arg': asciiEscapeJson({ path: file.id }) },
  });
  const blob = await response.blob();
  return {
    blob,
    name: file.name,
    mimeType: file.mimeType,
    size: blob.size,
    modifiedTime: file.modifiedTime,
  };
};
